import { useTranslation } from 'module/hooks/use-translation';
import { Form } from 'module/ui/components/atoms/form';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { TableDataType } from 'module/ui/components/atoms/rich-text-editor/toolbar/table-toolbar';
import { faCheck, faTrash } from '@fortawesome/pro-solid-svg-icons';
import { Button, InputField, Textarea } from '@rocketlane/ui';
import { NodeViewContent } from '@tiptap/react';
import { CustomSectionState } from 'api/custom-section/types';
import { AnswerLength } from 'api/custom-section/types/question-answer-item';
import { useCallback, useState } from 'react';
import { FormattedMessage } from 'react-intl';
import styled from 'styled-components';
import {
  SectionPreview,
  SectionTitle,
} from 'components/organisms/document-editor/extensions/custom-section/ui/shared/section-styles';
import { QuestionAnswerItemSectionComponentProps } from '../types';

const FormWrapper = styled.div`
  padding: var(--spacing-05);
  border: 1px solid var(--field-border-default);
  border-radius: var(--border-radius-small);
`;

const Footer = styled.div`
  display: flex;
  gap: var(--spacing-03);
  align-items: center;
  justify-content: flex-end;
  margin-top: var(--spacing-05);
`;

const RemoveButton = styled(Button)`
  &:hover {
    color: var(--red-60) !important;
  }
`;

type FormValues = {
  question: string;
  answer: string;
  dataType: TableDataType;
  answerLength?: AnswerLength | null;
  answerFrom: string;
};

function defaultAnswerLengthForDataType(
  dataType: TableDataType | undefined,
  stored: AnswerLength | null | undefined,
): AnswerLength | null {
  if (dataType === TableDataType.TEXT || dataType == null) {
    return stored ?? AnswerLength.TwentyWords;
  }

  return null;
}

export const QuestionAnswerItemPromptSection = ({
  node,
  onRemove,
  onDone,
  onUndo,
  contentEditable,
  onOpen,
}: QuestionAnswerItemSectionComponentProps) => {
  const showPrompt = node.attrs.state === CustomSectionState.Open;
  const [form] = Form.useForm<FormValues>();
  const [initialValues] = useState<FormValues>({
    question: node.attrs.config?.question ?? '',
    answer: node.attrs.config?.answer ?? '',
    dataType:
      node.attrs.config?.dataType ?? (TableDataType.TEXT as TableDataType),
    answerLength: defaultAnswerLengthForDataType(
      node.attrs.config?.dataType,
      node.attrs.config?.answerLength,
    ),
    answerFrom: node.attrs.config?.answerFrom ?? '',
  });

  const defaultTitleLabel = useTranslation(
    'rich_text_editor.sections.question_section.title',
  );

  const onGenerate = useCallback(() => {
    form.validateFields().then((values) => {
      const attributes = {
        ...node.attrs,
        state: CustomSectionState.Closed,
        title: node.attrs.title || values.question,
        config: {
          question: values.question,
          answer: values.answer,
          dataType: values.dataType,
          answerLength: defaultAnswerLengthForDataType(
            values.dataType,
            values.answerLength,
          ),
          answerFrom: values.answerFrom,
        },
      };

      onDone(attributes);
    });
  }, [form, node.attrs, onDone]);

  const onClose = useCallback(() => {
    if (node.content?.size === 0) {
      onRemove();

      return;
    }
    form.setFieldsValue({
      question: node.attrs.config?.question ?? '',
      answer: node.attrs.config?.answer ?? '',
      dataType:
        node.attrs.config?.dataType ?? (TableDataType.TEXT as TableDataType),
      answerLength: defaultAnswerLengthForDataType(
        node.attrs.config?.dataType,
        node.attrs.config?.answerLength,
      ),
      answerFrom: node.attrs.config?.answerFrom ?? '',
    });
    onUndo();
  }, [onUndo, node.attrs, node.content?.size, onRemove, form]);

  return (
    <div>
      {showPrompt && contentEditable ? (
        <FormWrapper>
          <Form
            form={form}
            layout="vertical"
            initialValues={initialValues}
            onFinish={onGenerate}
          >
            <Form.Item
              name="question"
              label="Question"
              rules={[{ required: true }]}
            >
              <InputField placeholder="Enter question" />
            </Form.Item>
            <Form.Item name="answer" label="Answer">
              <Textarea placeholder="Answer" />
            </Form.Item>
            <Form.Item name="dataType" label="Data type">
              <InputField placeholder="e.g. TEXT" />
            </Form.Item>
            <Form.Item name="answerLength" label="Answer length">
              <InputField placeholder="Answer length" />
            </Form.Item>
            <Form.Item name="answerFrom" label="Answer from">
              <InputField placeholder="Answer from" />
            </Form.Item>
            <Footer>
              <RemoveButton
                leftIcon={<FontAwesomeIcon icon={faTrash} size={14} />}
                onClick={onRemove}
                variant="ghost"
              />
              <Button variant="ghost" onClick={onClose}>
                <FormattedMessage id="undo" />
              </Button>
              <Button
                leftIcon={<FontAwesomeIcon icon={faCheck} size={14} />}
                onClick={() => form.submit()}
                variant="primary"
              >
                <FormattedMessage id="done" />
              </Button>
            </Footer>
          </Form>
        </FormWrapper>
      ) : (
        <SectionPreview onClick={onOpen}>
          <SectionTitle>
            {node.attrs.title ||
              node.attrs.config?.question ||
              defaultTitleLabel}
          </SectionTitle>
          <NodeViewContent />
        </SectionPreview>
      )}
    </div>
  );
};
